/**
 * 設定 → 自動ログアウト（#189、docs/76 ①。2026-10-16 射場さんの依頼）。管理者だけ（担当者は止めの画面 f＝app.tsx の ADMIN_ONLY_PAGES）。
 *   見出し「自動ログアウト」・説明「操作のない状態が続いた場合、自動的にログアウトします。」・ラジオ 30分／1時間／2時間／8時間（既定）／24時間・
 *   補足「変更は全ユーザーに適用されます。」・「取消」「決定」。
 *   全ユーザーに効く設定なので、選んだだけでは保存しない（設定の画面の自動保存の例外。10/16 射場さんに確かめた）。
 *   「決定」で PUT /company/auto-logout、「取消」は保存してある値に戻す。変えていなければどちらも押せない。PC 専用にしない
 */
import { AUTO_LOGOUT_LABEL, AUTO_LOGOUT_MINUTES, AUTO_LOGOUT_MSG, DEFAULT_AUTO_LOGOUT_MINUTES, type AutoLogoutSettings } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, messageOf } from "../api.js";
import { useBackNav, usePageName } from "../components/BackButton.jsx";
import { GroupCheckRow, GroupList, GroupSection } from "../components/GroupList.jsx";
import { SettingsHead, useRestPageClass } from "../components/SettingsHead.jsx";
import { ErrorText, useToast } from "../components/ui.jsx";

export const AUTO_LOGOUT_KEY = ["company-auto-logout"] as const;
type Minutes = AutoLogoutSettings["minutes"];

export function SettingsAutoLogoutPage() {
  const qc = useQueryClient();
  const { toast, node } = useToast();
  const q = useQuery({ queryKey: AUTO_LOGOUT_KEY, queryFn: () => api<AutoLogoutSettings>("GET", "/company/auto-logout") });
  const [picked, setPicked] = useState<Minutes | null>(null);
  const saved = q.data?.minutes ?? null;
  const cur = picked ?? saved;
  const changed = picked !== null && picked !== saved;
  // 選んだまま離れるときは確かめる（保存していない＝決定を押していない）
  const back = useBackNav({ to: "/settings", label: "設定" }, changed);
  usePageName(AUTO_LOGOUT_MSG.title);
  const cls = useRestPageClass("autologoutpage");

  const save = useMutation({
    mutationFn: (minutes: Minutes) => api<AutoLogoutSettings>("PUT", "/company/auto-logout", { minutes }),
    onSuccess: (v) => {
      qc.setQueryData(AUTO_LOGOUT_KEY, v);
      setPicked(null);
      // ME の上限（帯の期限）も取り直す
      void qc.invalidateQueries({ queryKey: ["me"] });
      toast(AUTO_LOGOUT_MSG.saved(AUTO_LOGOUT_LABEL[v.minutes]));
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });

  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!q.data || cur === null) return <p className="hint">読み込んでいます…</p>;
  return (
    <div className={cls} data-testid="autologout-settings">
      <SettingsHead back={back} title={AUTO_LOGOUT_MSG.title} />
      <p className="est-note" id="autologoutLead" data-testid="autologout-lead">
        {AUTO_LOGOUT_MSG.lead}
      </p>
      <GroupSection note={<span data-testid="autologout-note">{AUTO_LOGOUT_MSG.note}</span>} data-testid="autologout-sec">
        <div role="radiogroup" aria-labelledby="autologoutLead">
        <GroupList id="autologoutList">
          {AUTO_LOGOUT_MINUTES.map((m) => (
            <GroupCheckRow
              key={m}
              radioName="autoLogoutMinutes"
              title={`${AUTO_LOGOUT_LABEL[m]}${m === DEFAULT_AUTO_LOGOUT_MINUTES ? AUTO_LOGOUT_MSG.defaultMark : ""}`}
              checked={cur === m}
              onChange={() => setPicked(m)}
              disabled={save.isPending}
              buttonTestId={`autologout-${m}`}
            />
          ))}
        </GroupList>
        </div>
      </GroupSection>
      <div className="autologout-acts">
        <button type="button" className="btn" disabled={!changed || save.isPending} onClick={() => setPicked(null)} data-testid="autologout-cancel">
          {AUTO_LOGOUT_MSG.cancel}
        </button>
        <button type="button" className="btn primary" disabled={!changed || save.isPending} onClick={() => picked !== null && save.mutate(picked)} data-testid="autologout-confirm">
          {AUTO_LOGOUT_MSG.confirm}
        </button>
      </div>
      {node}
    </div>
  );
}
